// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import { Clickable } from "@insite/mobius/index";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { buttonTransparent } from "@rc/XDL/PreStyle/button";
import { xMask } from "@rc/XDL/Utilities";
import { color } from "@rc/XDL/Utilities";
import * as React from "react";
import { useEffect, useState } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

export interface PaginationPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        arrowButtons?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];

        // xng-change

        /* if true it will only show the page numbers */
        isBottom?: boolean;
        /** labelPosition for # items per page */
        labelPosition?: "left" | "top";
        /** hidePerPage for # items per page */
        hidePerPage?: boolean;
        /** hideSteps to hide pagination navigation */
        hideSteps?: boolean;
    }
>;

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

// XNG-Changes: Update style
const iconButtonStyles = css`
    justify-content: center;
    &[data-test-selector="pagination_previousButton"] {
        padding: 10px 14px;
        margin: 0;
    }
    &[data-test-selector="pagination_nextButton"] {
        padding: 10px 0 10px 15px;
        margin: 0;
    }

    & > ${IconWrapper} {
        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
    }

    &:disabled {
        opacity: 0.33;
    }

    && {
        ${buttonTransparent};
    }
`;

const PaginationStyle = styled.div<InjectableCss>`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    margin: 4px 0;
                `,
                null,
                null,
                css`
                    margin: 8px 0;
                `,
                null,
            ],
            "min",
        )};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, null, null, null], "max")};
    ul {
        display: flex;
        align-items: center;
        list-style: none;
    }
    li {
        display: inline-block;
    }
    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, null, null, null], "max")};
    ${injectCss}
`;

const Ellipsis = styled.li<InjectableCss>`
    ${injectCss}
`;

const StepsWrapper = styled.div<InjectableCss>`
    display: flex;
    align-items: center;

    ${FormFieldStyle} {
        width: 44px;
        margin-right: 8px;

        input {
            height: 29px;
            width: 38px;
            min-height: 29px;
            max-height: 29px;
            border: 1px solid ${color("common.ui5")};
            border-radius: 2px;
            background-color: ${color("common.ui1")};
            text-align: center;
            margin-right: 8px;
            display: block;
            padding: 13px 0 13px 0;

            &:focus {
                border: 1px solid ${color("common.ui5")} !important;
                border-radius: 2px;
                padding: 13px 0 13px 0;
            }
        }
    }

    ${TypographyStyle} {
        color: ${color("text.main")};
        font-size: 14px;
        letter-spacing: 0;
        line-height: 22px;
        font-weight: 300;
        margin-top: -1px;
    }
`;

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage: possibleStringCurrentPage,
        onChangeResultsPerPage,
        onChangePage,
        hidePerPage,
        hideSteps,
        isBottom,
        resultsPerPage: possibleStringResultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        labelPosition = "top",
        ...otherProps
    } = props;

    const currentPage = convertToNumberIfString(possibleStringCurrentPage);
    const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
    };

    const { spreadProps, applyProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const navIconsSrc = spreadProps("navIconsSrc");

    const [moveToField, setMoveToField] = useState(currentPage || "");

    useEffect(() => {
        setMoveToField(currentPage);
    }, [currentPage]);

    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
    let pagesToDisplay: ("e" | number)[] = [];
    if (finalPageIndex < 6) {
        for (let i = 1; i <= finalPageIndex; i += 1) {
            pagesToDisplay.push(i);
        }
    } else if (currentPage < 4) {
        pagesToDisplay = [1, 2, 3, 4, "e", finalPageIndex];
    } else if (currentPage >= 4 && currentPage < finalPageIndex - 2) {
        pagesToDisplay = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, "e", finalPageIndex];
    } else {
        pagesToDisplay = [
            "e",
            finalPageIndex - 4,
            finalPageIndex - 3,
            finalPageIndex - 2,
            finalPageIndex - 1,
            finalPageIndex,
        ];
    }

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navIcon = (
        pageIndex: number,
        icon: string | React.ComponentType,
        ariaLabel: string,
        disabledFlag: boolean,
        dataTestSelector?: string,
    ) => (
        <Clickable
            variant="tertiary"
            {...buttonDisplayProps(props, {
                page: -1,
                moreCss: iconButtonStyles,
                cssOverrides,
                buttonProps,
                currentPageButtonVariant,
            })}
            aria-label={ariaLabel || "pagination-page"}
            disabled={disabledFlag}
            onClick={() => {
                setMoveToField(pageIndex);
                onChangePage(pageIndex);
            }}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
        >
            <VisuallyHidden>{ariaLabel}</VisuallyHidden>
            <ButtonIcon size={25} src={icon} />
        </Clickable>
    );

    const moveToFieldValidation = (valueAsString: string) => {
        const value = Number(valueAsString);
        let formatedValue;
        if (!valueAsString || !Number.isInteger(value)) {
            formatedValue = xMask.qty("1");
            setMoveToField(formatedValue);
            return formatedValue;
        }
        if (value < 1) {
            formatedValue = xMask.qty("1");
            setMoveToField(formatedValue);
            return formatedValue;
        }
        if (value > finalPageIndex) {
            formatedValue = xMask.qty(finalPageIndex?.toString());
            setMoveToField(formatedValue);
            return formatedValue;
        }
        if (value === currentPage) {
            return false;
        }
        return value;
    };

    const onMoveToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMoveToField(xMask.qty(event.currentTarget.value));
    };

    const onMoveToKeyUpHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            onMoveToBlurHandler(event as any);
        }
    };

    const onMoveToBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const result = moveToFieldValidation(event.currentTarget.value);
        if (result) {
            onChangePage(result);
        }
    };

    const paginationSteps = (finalPageIndex: number) => (
        <StepsWrapper>
            <TextField
                labelPosition="top"
                value={moveToField}
                onChange={onMoveToChangeHandler}
                onKeyUp={onMoveToKeyUpHandler}
                onBlur={onMoveToBlurHandler}
            />
            <Typography>{`${translate("of")} ${finalPageIndex}`}</Typography>
        </StepsWrapper>
    );

    return (
        <PaginationStyle css={cssOverrides.pagination} {...omitSingle(otherProps, "cssOverrides")}>
            {!isBottom && !hidePerPage && resultsPerPageOptions?.length > 0 && (
                <PerPageSelect css={cssOverrides.perPageSelect}>
                    <Select
                        onChange={changeResultsPerPage}
                        label={resultsPerPageLabel}
                        labelPosition={labelPosition}
                        preventEllipsis={true}
                        value={resultsPerPage}
                        {...spreadProps("selectProps" as any)}
                        data-test-selector="pagination_resultsPerPage"
                        aria-label="pagination-per-page-select"
                    >
                        {resultsPerPageOptions.map((n: string) => (
                            <option key={n} value={n}>
                                {n} {translate("Per Page")}
                            </option>
                        ))}
                    </Select>
                </PerPageSelect>
            )}
            {!hideSteps && (
                <LinkList
                    role="navigation"
                    aria-label={translate("Pagination navigation")}
                    css={cssOverrides.linkList}
                    data-test-selector={`pagination_currentPage_${currentPage}`}
                >
                    <ul>
                        <li>
                            {navIcon(
                                currentPage - 1,
                                navIconsSrc.previousPage,
                                translate("Previous page"),
                                backDisabled,
                                "pagination_previousButton",
                            )}
                        </li>
                        <li>{paginationSteps(finalPageIndex)}</li>
                        <li>
                            {navIcon(
                                currentPage + 1,
                                navIconsSrc.nextPage,
                                translate("Next page"),
                                forwardDisabled,
                                "pagination_nextButton",
                            )}
                        </li>
                    </ul>
                </LinkList>
            )}
        </PaginationStyle>
    );
});

/** @component */
export default Pagination as React.ComponentType<PaginationComponentProps>;

export { Pagination, PaginationStyle };
